/**
 * Office.js failure formatting.
 *
 * An `OfficeExtension.Error` stringifies to the generic "Sorry, something went
 * wrong. Check the OfficeExtension.Error.debugInfo for more information." — the
 * actionable part (the error code and the statement that failed) lives on
 * `.code` / `.debugInfo`, which the default `.message` drops. Surface those so an
 * apply failure is diagnosable instead of opaque.
 */

/** The subset of `OfficeExtension.Error` we read, duck-typed for runtime safety. */
interface OfficeErrorLike {
  code?: string
  debugInfo?: {
    code?: string
    message?: string
    errorLocation?: string
  }
}

/**
 * Build a user-facing message for a caught error.
 *
 * Our own errors (e.g. `AnchorNotFoundError`) carry a friendly message and no
 * Office error code, so their message is used verbatim. A raw Office.js failure
 * has no useful message but does carry a `code` (e.g. "GeneralException") and an
 * `errorLocation` (e.g. "Range.insertHtml") — append those to `fallback` so the
 * card shows what actually failed.
 */
export function describeOfficeError(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) return fallback
  const office = error as Error & OfficeErrorLike
  const code = office.debugInfo?.code || office.code
  const location = office.debugInfo?.errorLocation
  // No Office code/location → this is one of our own errors; trust its message.
  if (!code && !location) return error.message || fallback
  const detail = [code, location && `at ${location}`].filter(Boolean).join(' ')
  return detail ? `${fallback} (${detail})` : fallback
}
